import { withCsrfInit } from '../csrf';
import { fetchAuthedApiResponse, fetchPublicApiResponse } from './http';

function withQuery(path: string, params?: URLSearchParams): string {
  const query = params?.toString() ?? '';
  return query ? `${path}?${query}` : path;
}

function sensitiveWorkflowGet(path: string, signal?: AbortSignal): Promise<Response> {
  return fetchAuthedApiResponse(path, { cache: 'no-store', skipDedupe: true, signal });
}

export function fetchReceptionBookingsResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery('/reception/api/reports/bookings', params), signal);
}

export function fetchReceptionGroupReportsResponse(groupId: number | undefined, params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery(`/reception/api/reports/groups${groupId === undefined ? '' : `/${groupId}`}`, params), signal);
}

export async function cancelReceptionAttendanceSessionResponse(
  sessionId: number,
  reason: string,
): Promise<Response> {
  return fetchPublicApiResponse(
    `/reception/api/attendance/sessions/${sessionId}/cancel`,
    await withCsrfInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason.trim().slice(0, 1000) }),
    }),
  );
}

export async function updateReceptionAttendanceSessionMeetingLinkResponse(
  sessionId: number,
  meetingLink: string,
): Promise<Response> {
  return fetchPublicApiResponse(
    `/reception/api/attendance/sessions/${sessionId}/meeting-link`,
    await withCsrfInit({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meeting_link: meetingLink }),
    }),
  );
}

export function createReceptionAttendanceGroupSessionResponse(
  groupId: number,
  payload: { title: string; description?: string; date?: string },
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/attendance/group/${groupId}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function markReceptionAttendanceSessionStudentResponse(
  sessionId: number,
  payload: { student_id: number; status: string; notes?: string; grade?: number | null },
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/attendance/sessions/${sessionId}/mark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function quickPayReceptionAttendanceSessionStudentResponse(
  sessionId: number,
  studentId: number,
  payload: {
    mode: 'quick_invoice' | 'installment';
    payment_purpose?: string;
    operation_id?: string;
    service_id?: number;
    amount?: number;
    installment_id?: number;
    payment_method: string;
  },
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/attendance/sessions/${sessionId}/students/${studentId}/quick-payment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function fetchReceptionCourseInterestsResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery('/reception/api/course-interests', params), signal);
}

export async function notifyReceptionCourseInterestResponse(id: number, note?: string): Promise<Response> {
  const formData = new FormData();
  if (note && note.trim()) formData.set('note', note.trim());
  return fetchAuthedApiResponse(
    `/reception/api/course-interests/${id}/notify`,
    await withCsrfInit({ method: 'POST', body: formData }),
  );
}

export async function rejectReceptionCourseInterestResponse(id: number, note?: string): Promise<Response> {
  const formData = new FormData();
  if (note && note.trim()) formData.set('note', note.trim());
  return fetchAuthedApiResponse(
    `/reception/api/course-interests/${id}/reject`,
    await withCsrfInit({ method: 'POST', body: formData }),
  );
}

export function fetchReceptionExamListResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return fetchAuthedApiResponse(withQuery('/reception/api/exams', params), {
    cache: 'no-store',
    skipDedupe: true,
    signal,
  });
}

export function toggleReceptionExamVisibilityResponse(id: number): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/exams/${id}/visibility`, { method: 'POST' });
}

export function setReceptionExamStateResponse(id: number, active: boolean): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/exams/${id}/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
}

export function deleteReceptionExamResponse(id: number): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/exams/${id}`, { method: 'DELETE' });
}

export function cloneReceptionExamResponse(
  id: number,
  payload: { title_suffix: string; make_active: boolean },
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/exams/${id}/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function fetchReceptionPlacementPaymentsResponse(params: URLSearchParams): Promise<Response> {
  return fetchAuthedApiResponse(withQuery('/reception/api/placement-payments', params));
}

export function fetchReceptionPlacementPaymentScopeResponse(options?: { signal?: AbortSignal }): Promise<Response> {
  return fetchAuthedApiResponse('/reception/api/placement-payments/scope', {
    cache: 'no-store',
    skipDedupe: true,
    signal: options?.signal,
  });
}

export function fetchReceptionPlacementPaymentDetailResponse(paymentId: number, options?: { signal?: AbortSignal }): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/placement-payments/${paymentId}`, {
    cache: 'no-store',
    skipDedupe: true,
    signal: options?.signal,
  });
}

export function fetchReceptionPlacementPaymentStatsResponse(): Promise<Response> {
  return fetchAuthedApiResponse('/reception/api/placement-payments/stats');
}

export function approveReceptionPlacementPaymentResponse(paymentId: number): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/placement-payments/${paymentId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export function rejectReceptionPlacementPaymentResponse(paymentId: number, reason: string): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/placement-payments/${paymentId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function fetchReceptionRequestsFiltersResponse(signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet('/reception/api/requests/filters', signal);
}

export function fetchReceptionRequestsResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery('/reception/api/requests', params), signal);
}

export async function approveReceptionRequestResponse(id: number, note?: string): Promise<Response> {
  const formData = new FormData();
  if (note?.trim()) formData.append('note', note.trim());
  return fetchAuthedApiResponse(
    `/reception/api/requests/${id}/approve`,
    await withCsrfInit({ method: 'POST', body: formData }),
  );
}

export async function rejectReceptionRequestResponse(id: number, note?: string): Promise<Response> {
  const formData = new FormData();
  if (note?.trim()) formData.append('note', note.trim());
  return fetchAuthedApiResponse(
    `/reception/api/requests/${id}/reject`,
    await withCsrfInit({ method: 'POST', body: formData }),
  );
}

export function fetchReceptionSalesConsultationSlotsResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery('/reception/api/sales-consultation-slots', params), signal);
}

export function createReceptionSalesConsultationSlotResponse(payload: Record<string, unknown>): Promise<Response> {
  return fetchAuthedApiResponse('/reception/api/sales-consultation-slots', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updateReceptionSalesConsultationSlotResponse(
  slotId: number,
  payload: Record<string, unknown>,
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/sales-consultation-slots/${slotId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function deleteReceptionSalesConsultationSlotResponse(slotId: number): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/sales-consultation-slots/${slotId}`, {
    method: 'DELETE',
  });
}

export function fetchReceptionBranchesResponse(signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet('/reception/api/branches', signal);
}

export function fetchReceptionCoursesResponse(signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet('/reception/api/courses', signal);
}

export async function fetchReceptionScopedCourses(signal?: AbortSignal): Promise<string[]> {
  const response = await fetchReceptionCoursesResponse(signal);
  if (!response.ok) throw new Error(`Failed to load reception courses (${response.status})`);
  const payload = await response.json() as { courses?: unknown };
  if (!Array.isArray(payload.courses)) return [];
  return payload.courses
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function registerReceptionStudentResponse(payload: FormData): Promise<Response> {
  return fetchAuthedApiResponse('/reception/api/students/register', {
    method: 'POST',
    body: payload,
  });
}

export function resendReceptionStudentSetPasswordEmailResponse(studentId: number): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/students/${studentId}/resend-set-password-email`, {
    method: 'POST',
  });
}

export function fetchReceptionSalesConsultQueueResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery('/reception/api/sales-consult-queue', params), signal);
}

export function updateReceptionSalesConsultQueueChoiceResponse(
  choiceId: number,
  payload: { status: string },
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/sales-consult-queue/${choiceId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function fetchReceptionDailyReportsResponse(params: URLSearchParams, signal?: AbortSignal): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/daily-reports?${params.toString()}`, {
    cache: 'no-store',
    skipDedupe: true,
    signal,
  });
}

export function fetchReceptionStaffActivityResponse(params: URLSearchParams, employeeId?: number, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(withQuery(`/reception/api/daily-reports/staff-activity${employeeId ? `/${employeeId}` : ''}`, params), signal);
}

export async function markReceptionDailyReportReadResponse(reportId: number): Promise<Response> {
  return fetchPublicApiResponse(
    '/reception/api/daily-reports/mark-read',
    await withCsrfInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report_id: reportId }),
    }),
  );
}

export function fetchReceptionDailySummaryResponse(): Promise<Response> {
  return fetchAuthedApiResponse('/reception/api/daily-summary');
}

export async function acknowledgeReceptionDailySummaryResponse(): Promise<Response> {
  return fetchAuthedApiResponse(
    '/reception/api/daily-summary/acknowledge',
    await withCsrfInit({ method: 'POST' }),
  );
}

export function fetchReceptionCalendarSessionsResponse(
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<Response> {
  return fetchAuthedApiResponse(`/reception/api/calendar/sessions?${params.toString()}`, {
    cache: 'no-store',
    skipDedupe: true,
    signal,
  });
}

export async function updateReceptionCalendarSessionTimeResponse(
  sessionId: number,
  payload: Record<string, unknown>,
): Promise<Response> {
  return fetchPublicApiResponse(
    `/reception/api/calendar/sessions/${sessionId}/time`,
    await withCsrfInit({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  );
}

export function fetchReceptionAttendanceMatrixResponse(groupId: number, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(`/reception/api/attendance/matrix/${groupId}`, signal);
}

export function fetchReceptionAttendanceGroupsResponse(signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet('/reception/api/attendance/groups', signal);
}

export function fetchReceptionGroupAttendanceResponse(groupId: number, signal?: AbortSignal): Promise<Response> {
  return sensitiveWorkflowGet(`/reception/api/attendance/group/${groupId}`, signal);
}

export async function markReceptionAttendanceSessionStudentWithCsrfResponse(
  sessionId: number,
  payload: { student_id: number; status: string; notes?: string; grade?: number | null },
): Promise<Response> {
  return fetchPublicApiResponse(
    `/reception/api/attendance/sessions/${sessionId}/mark`,
    await withCsrfInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  );
}
